import type { CriteriaOption } from '../patient-surveys.interface';

export interface AutomatedSurvey {
  id: string;
  name: string;
  description: string;
  createdAt: Date;
  updatedAt: Date;
}
export interface JointsAndCategoriesOptions {
  id: string;
  code: string;
  name: string;
  type: 'checkbox' | 'radio';
}
export interface PatientCriteria {
  id: string;
  name: string;
  description: string;
  type: 'radio' | 'checkbox';
}
export type yearlyOccurrences = PatientCriteria;
export type sendDates = CriteriaOption;
export interface ProcedureType {
  id: string;
  name: string;
  description: string;
}
export interface Survey {
  id: string;
  name: string;
  description: string;
  children: Survey[];
}

/** Schedule option for baseline or appointment-triggered (matches backend BaselineOrAppointmentScheduleOptionDto). */
export interface BaselineOrAppointmentScheduleOption {
  daysPriorToAppt: number[];
}

/** Schedule option for recurrence by days or years (matches backend RecurrenceScheduleOptionDto). */
export interface RecurrenceScheduleOption {
  occurrenceType: 'DAYS' | 'YEARS';
  byDays?: {
    repeatEvery: number;
    endsAfter?: number;
  };
  byYears?: {
    yearOccurrences: number[];
    sendDates: number[];
  };
}

/** Request schedule shape (matches backend ScheduleDto). time and sendTimeZone are on the request root. */
export interface AutomatedSurveyScheduleDto {
  baselineOrAppointment?: BaselineOrAppointmentScheduleOption;
  recurrence?: RecurrenceScheduleOption;
}

/** Base request shape shared by create and update (matches BaseAutomatedSurveyDto). */
export interface BaseAutomatedSurveyRequest {
  name: string;
  jointTypeId: string;
  categoryId: string;
  sendTimeZone: string;
  time: string;
  schedule: AutomatedSurveyScheduleDto;
  deliveryMethodId: string;
  smsTemplateId?: string;
  emailTemplateId?: string;
  appointmentTypeId?: string;
}

/** Create request (matches CreateAutomatedSurveyDto). */
export interface CreateAutomatedSurveyRequest extends BaseAutomatedSurveyRequest {
  surveyTypeId: string;
  status?: 'ACTIVE' | 'INACTIVE';
}

/** Update request (matches UpdateAutomatedSurveyDto). status required; no surveyTypeId. */
export interface UpdateAutomatedSurveyRequest extends BaseAutomatedSurveyRequest {
  status: 'ACTIVE' | 'INACTIVE';
}

/** Response shape for GET /tenant/{tenantId}/automated-survey/{automatedSurveyId} (SOPMP-476 metadata). Form and GET mapping use flat schedule. */
export interface AutomatedSurveyDetail {
  id: string;
  name: string;
  surveyTypeId: string;
  jointTypeId: string;
  categoryId: string;
  deliveryMethodId: string;
  schedule: {
    occurrenceType: 'DAYS' | 'YEARS';
    time: string;
    sendTimeZone: string;
    daysPriorToAppt?: number[];
    byDays?: {
      repeatEvery: number;
      endsAfter?: number;
    };
    byYears?: {
      yearOccurrences: number[];
      sendDates: number[];
    };
  };
  status: 'ACTIVE' | 'INACTIVE';
  smsTemplateId?: string;
  emailTemplateId?: string;
  createdOn?: string;
  createdBy?: string;
  modifiedOn?: string;
  modifiedBy?: string;
  lastRun?: string;
}

/**
 * Raw API response when backend returns typeId + appointmentTriggered/anniversaryTriggered
 * instead of a flat schedule. Used to normalize to AutomatedSurveyDetail.
 */
export interface AutomatedSurveyApiDetail {
  id?: string;
  typeId?: string;
  name?: string;
  status?: string;
  jointTypeId?: string;
  categoryId?: string;
  deliveryMethodId?: string;
  smsTemplateId?: string;
  emailTemplateId?: string;
  /** Backend GET returns createDate/createBy/modifyDate/modifyBy (scheduler-service). */
  createDate?: string;
  createBy?: string;
  modifyDate?: string;
  modifyBy?: string;
  createdOn?: string;
  createdBy?: string;
  modifiedOn?: string;
  modifiedBy?: string;
  lastRun?: string;
  appointmentTriggered?: {
    time?: string;
    sendTimeZone?: string;
    daysPriorToAppt?: number | number[];
    /** Alternate key some backends may return (e.g. daysPrior). */
    daysPrior?: number | number[];
    byDays?: { repeatEvery?: number; endsAfter?: number };
  };
  anniversaryTriggered?: {
    time?: string;
    sendTimeZone?: string;
    /** Backend GET returns this (scheduler-service). */
    occurrenceTypeId?: string;
    occurrenceType?: 'DAYS' | 'YEARS';
    byDays?: {
      repeatEvery?: number;
      endsAfter?: number;
      time?: string;
      sendTimeZone?: string;
    };
    byYears?: {
      yearOccurrences?: number[];
      sendDates?: number[];
      time?: string;
      sendTimeZone?: string;
    };
  };
}

function resolveScheduleTimeAndZone(
  appointment: AutomatedSurveyApiDetail['appointmentTriggered'],
  anniversary: AutomatedSurveyApiDetail['anniversaryTriggered'],
  occurrenceType: 'DAYS' | 'YEARS'
): { time: string; sendTimeZone: string } {
  if (appointment) {
    return {
      time: appointment.time ?? '09:00:00',
      sendTimeZone: appointment.sendTimeZone ?? '',
    };
  }

  if (anniversary) {
    const nestedSchedule = occurrenceType === 'YEARS' ? anniversary.byYears : anniversary.byDays;
    return {
      time: nestedSchedule?.time ?? anniversary.time ?? '09:00:00',
      sendTimeZone: nestedSchedule?.sendTimeZone ?? anniversary.sendTimeZone ?? '',
    };
  }

  return { time: '09:00:00', sendTimeZone: '' };
}

export function mapApiDetailToAutomatedSurveyDetail(raw: AutomatedSurveyApiDetail): AutomatedSurveyDetail {
  const appointment = raw.appointmentTriggered;
  const anniversary = raw.anniversaryTriggered;
  const scheduleSource = appointment ?? anniversary;
  const rawOccurrence = anniversary?.occurrenceTypeId ?? anniversary?.occurrenceType ?? 'DAYS';
  const occurrenceType: 'DAYS' | 'YEARS' = rawOccurrence === 'YEARS' ? 'YEARS' : 'DAYS';
  const { time, sendTimeZone } = resolveScheduleTimeAndZone(appointment, anniversary, occurrenceType);
  const schedule: AutomatedSurveyDetail['schedule'] = {
    occurrenceType,
    time,
    sendTimeZone,
  };
  if (appointment?.daysPriorToAppt != null) {
    schedule.daysPriorToAppt = Array.isArray(appointment.daysPriorToAppt)
      ? appointment.daysPriorToAppt
      : [appointment.daysPriorToAppt];
  } else if (appointment?.daysPrior != null) {
    schedule.daysPriorToAppt = Array.isArray(appointment.daysPrior) ? appointment.daysPrior : [appointment.daysPrior];
  }
  if (scheduleSource?.byDays) {
    schedule.byDays = {
      repeatEvery: scheduleSource.byDays.repeatEvery ?? 1,
      endsAfter: scheduleSource.byDays.endsAfter,
    };
  }
  const byYears = anniversary?.byYears;
  if (byYears) {
    schedule.byYears = {
      yearOccurrences: byYears.yearOccurrences ?? [],
      sendDates: byYears.sendDates ?? [],
    };
  }
  return {
    id: raw.id ?? '',
    name: raw.name ?? '',
    surveyTypeId: raw.typeId ?? '',
    jointTypeId: raw.jointTypeId ?? '',
    categoryId: raw.categoryId ?? '',
    deliveryMethodId: raw.deliveryMethodId ?? '',
    schedule,
    status: raw.status?.toLowerCase() === 'active' ? 'ACTIVE' : 'INACTIVE',
    smsTemplateId: raw.smsTemplateId,
    emailTemplateId: raw.emailTemplateId,
    createdOn: raw.createDate ?? raw.createdOn,
    createdBy: raw.createBy ?? raw.createdBy,
    modifiedOn: raw.modifyDate ?? raw.modifiedOn,
    modifiedBy: raw.modifyBy ?? raw.modifiedBy,
    lastRun: raw.lastRun,
  };
}
export interface AutomatedSurveyOptions {
  categories: Options[];
  jointTypes: Options[];
  methods: Options[];
  statuses: string[];
  types: Options[];
}
export interface Options {
  id?: string;
  code: string;
  name: string;
}
